import { useState } from 'react';
import { DownloadIcon, RefreshCwIcon, Trash2Icon } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import type {
  LibraryInstallResult,
  LibraryItemView,
} from '@/lib/api/generated/model';
import {
  useAssignablePeople,
  useCurrentUser,
  useIsOrgAdmin,
} from '@/features/identity';
import { useLlmProfiles } from '@/features/llm-profiles';
import {
  ResolveReferences,
  initialAnswers,
  outstanding,
  toRequest,
  toRows,
  type Answers,
} from '@/features/workflows';
import { actionFor, canRemove, removalBlockedBecause } from '../actions';
import { clearInstallReport } from '../installReports';
import type { useInstallLibraryItem } from '../hooks/useInstallLibraryItem';
import { useLibraryFile } from '../hooks/useLibraryFile';
import { useStaggered } from '../hooks/useStaggered';
import { installTree } from '../installTree';
import { ownFileOf } from '../itemFiles';
import { takenDownMessage } from '../takeDownWords';
import { TakeDownConfirm } from './TakeDownConfirm';
import { useUninstallLibraryItem } from '../hooks/useUninstallLibraryItem';
import { usedBy } from '../usedBy';
import { kindLabel } from '../labels';
import { missingParts, missingPartsLabel } from '../missingParts';
import { summarizeInstall } from '../summarizeInstall';
import { workflowOutline } from '../workflowOutline';
import { InlineCode } from './InlineCode';
import { ItemPreview } from './ItemPreview';
import { InstallOutcomes } from './InstallOutcomes';
import { InstallTree } from './InstallTree';
import { KindMonogram } from './KindMonogram';
import { StateBadge } from './StateBadge';
import { UsedBy } from './UsedBy';
import { useCanWork } from '@/features/workspaces/canWork';
import { displayNameOf } from '@/shared/components/accountIdentity';

/**
 * One item, in full: the description, what it needs, and the one action its
 * state allows (spec 104 US2).
 *
 * A drawer rather than a dialog because dependency lists and outcome lists
 * read top to bottom and can be long. The page keys this by the item, so a
 * different item gets fresh state without an effect.
 */
/** The Library installs; only the import dialog imports (spec 104 restyle). */
const INSTALLING = {
  noun: 'this workflow',
  done: 'installed',
  verb: 'install',
};

/** The workflows a definition's linked steps name, or none it cannot read. */
function ownLinks(definition: string | undefined): string[] {
  if (definition === undefined) return [];
  const outline = workflowOutline(definition);
  return (outline?.phases ?? [])
    .flatMap((phase) => phase.steps)
    .flatMap((step) => (step.links === null ? [] : [step.links]));
}

export function LibraryItemDrawer({
  workspaceId,
  item,
  items,
  open,
  onClose,
  installer,
  report,
  fresh,
  onOpenItem,
}: {
  workspaceId: string;
  item: LibraryItemView | undefined;
  items: readonly LibraryItemView[];
  open: boolean;
  onClose: () => void;
  installer: ReturnType<typeof useInstallLibraryItem>;
  /**
   * What the last install of this item did, whether it happened on this mount
   * or on an earlier one the reader has come back from (spec 108 FR-006).
   */
  report: LibraryInstallResult | undefined;
  /**
   * Whether that report was produced here, now.
   *
   * It gates the choreography and nothing else. A report being read again is a
   * record of the past, and replaying the install on it would state something
   * false — that an install is running (FR-007).
   */
  fresh: boolean;
  /**
   * Show another item in this drawer, as a place the reader can come back from
   * (spec 109 FR-003, FR-014).
   */
  onOpenItem?: (item: LibraryItemView) => void;
}) {
  // Answers to what a library workflow names and this deployment lacks, held
  // here while the rows are on screen and discarded with them (spec 104 US4).
  const [answers, setAnswers] = useState<Answers | null>(null);
  // Two presses to remove something: the first asks, the second does it. A
  // confirmation in the footer rather than a dialog over a dialog, which is
  // where a sheet's focus trapping starts fighting itself.
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  const remover = useUninstallLibraryItem(workspaceId);
  const canWork = useCanWork();
  const removable = item !== undefined && canRemove(item);
  // A question nobody can answer any more is not asked: when the item stops
  // being removable — it was removed, or the catalog moved under the drawer —
  // the confirmation goes with it. Adjusted during render, as React
  // recommends, rather than in an effect that would render twice.
  const [confirmable, setConfirmable] = useState(removable);
  if (confirmable !== removable) {
    setConfirmable(removable);
    if (!removable) setConfirmingRemoval(false);
  }
  const profiles = useLlmProfiles();
  const people = useAssignablePeople(workspaceId);
  const isOrgAdmin = useIsOrgAdmin();
  // Derived before the early return, because the stagger is a hook and hooks
  // cannot be skipped: an install that reports on a drawer with no item still
  // has to count to zero.
  const blocked = report?.blocked === true;
  const hasReport = report !== undefined && !blocked;
  // Only a report made here is watched. A restored one animates nothing, so no
  // timer starts at all — the same standing-down `useStaggered` already does for
  // reduced motion (spec 108 research R3).
  const animate = hasReport && fresh;
  const settledCount = useStaggered(item?.requires?.length ?? 0, animate);
  // The workflow's own definition, for the links its steps make: the outline
  // reads the same file with the same key, so this is the cached answer, not a
  // second request (spec 109 research R3, SC-005).
  const definition = useLibraryFile(
    workspaceId,
    item?.kind ?? 'workflow',
    item?.key ?? '',
    item?.kind === 'workflow' ? ownFileOf(item) : undefined,
    item?.digest ?? '',
  );

  if (item === undefined) return null;
  const action = actionFor(item);
  const rows = blocked ? toRows(report?.unresolved ?? []) : [];
  const shownAnswers = answers ?? initialAnswers(rows);
  const canConfirm = blocked && outstanding(rows, shownAnswers).length === 0;
  // Each dependency beside the catalog item it names and what installing would
  // do to it; a name the library lacks is the manifest's mistake and says so.
  const summary = summarizeInstall(item, items);
  // The outcomes wait for the last row to settle, so the two do not compete
  // for the reader's attention (spec 105 FR-016).
  const sequenceDone = !animate || settledCount >= summary.rows.length;
  // Installed workflows that name this item. Removing it does not break them
  // today — a prompt naming a missing agent is the agent's problem at run
  // time — but somebody about to delete it should be told.
  // What this workflow names and the workspace no longer holds. Installing it
  // again restores exactly these and reports the workflow itself as already
  // installed, so the repair is the install the page already knows how to do.
  const gone = missingParts(item, items);
  // Every library workflow that uses this item, and the ones installed here
  // are the ones a removal names — one answer to "what needs this?" (spec 109
  // FR-016).
  const users = usedBy(item, items);
  const neededBy = users.filter((u) => u.present).map((u) => u.user.name);

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent side="right" className="flex w-full max-w-md flex-col">
        <SheetHeader>
          <div className="flex items-center gap-3">
            <KindMonogram kind={item.kind} size="sm" />
            <SheetTitle
              className={item.kind !== 'workflow' ? 'font-mono' : undefined}
            >
              {item.name}
            </SheetTitle>
            <StateBadge
              state={item.state}
              installable={item.installable}
              reason={item.reason}
            />
          </div>
          <SheetDescription>{kindLabel(item.kind)}</SheetDescription>
        </SheetHeader>

        <SheetBody className="flex flex-col gap-4">
          {/* The lead, a size above the body below it: the drawer used to be
              one size throughout, and the preview that now fills it needs
              something to sit under. */}
          <p className="max-w-prose text-base">
            <InlineCode text={item.description} />
          </p>

          {item.origin === 'shared' && (
            <SharedBy
              workspaceId={workspaceId}
              item={item}
              onTakenDown={onClose}
            />
          )}

          {!item.installable && item.reason && (
            <p role="status" className="text-destructive text-sm">
              {item.reason}
            </p>
          )}

          {/* What the item says, read on demand; never blocks Install. */}
          <ItemPreview
            workspaceId={workspaceId}
            item={item}
            items={items}
            onOpenItem={onOpenItem}
          />

          {item.kind === 'workflow' && summary.rows.length > 0 && (
            <section className="flex flex-col gap-2">
              <h3 className="text-sm font-medium">Needs</h3>
              <InstallTree
                tree={installTree(item, items, ownLinks(definition.file?.text))}
                // A row beside a restored report shows the dependency's
                // state, never the plan: "will be added" next to a report
                // that it *was* added is a lie about the present. The order is
                // the install's, so the tree still settles innermost first.
                settledFor={(order) =>
                  hasReport && (!fresh || order < settledCount)
                }
              />
              <p className="text-muted-foreground text-xs">
                Install adds {summary.toAdd.length}{' '}
                {summary.toAdd.length === 1 ? 'item' : 'items'}.
                {summary.leftAlone.length > 0 && (
                  <>
                    {' '}
                    {summary.leftAlone.length}{' '}
                    {summary.leftAlone.length === 1 ? 'is' : 'are'} already here
                    and {summary.leftAlone.length === 1 ? 'stays' : 'stay'} as{' '}
                    {summary.leftAlone.length === 1 ? 'it is' : 'they are'}.
                  </>
                )}
              </p>
            </section>
          )}

          <UsedBy entries={users} onOpenItem={onOpenItem} />

          {gone.length > 0 && (
            <section role="status" className="flex flex-col gap-2">
              <p className="text-state-warning text-sm font-medium">
                {missingPartsLabel(gone.length)}
              </p>
              <p className="text-muted-foreground text-sm">
                This workflow names {gone.map((dep) => dep.key).join(', ')},
                which this workspace no longer has. A run will not fail; the
                agent simply works without
                {gone.length === 1 ? ' it' : ' them'}.
              </p>
              {canWork ? (
                <div>
                  <Button
                    disabled={installer.pending}
                    onClick={() => {
                      installer.reset();
                      installer.install(item.kind, item.key);
                    }}
                  >
                    <DownloadIcon aria-hidden="true" />
                    Restore{' '}
                    {gone.length === 1
                      ? '1 missing item'
                      : `${String(gone.length)} missing items`}
                  </Button>
                </div>
              ) : null}
            </section>
          )}

          {item.kind === 'workflow' && item.state === 'update_available' && (
            <p className="text-muted-foreground text-sm">
              The library has a newer version of this workflow. A workflow is
              not updated in place: delete it and install it again.
            </p>
          )}

          {hasReport && sequenceDone && (
            <InstallOutcomes result={report} workspaceId={workspaceId} />
          )}

          {blocked && (
            <section className="flex flex-col gap-3">
              <p className="text-sm">
                This workflow names something this deployment does not have.
                Choose what to use instead, or cancel and nothing is installed.
              </p>
              <ResolveReferences
                subject={INSTALLING}
                rows={rows}
                answers={shownAnswers}
                onAnswersChange={setAnswers}
                profiles={profiles.entries ?? []}
                profilesUnavailable={profiles.loadFailed}
                canCreateProfiles={isOrgAdmin}
                people={people.people}
                directoryUnavailable={people.failed}
              />
              <div className="flex justify-end gap-2 max-md:flex-col-reverse max-md:[&>*]:w-full">
                <Button
                  variant="outline"
                  onClick={() => {
                    setAnswers(null);
                    installer.reset();
                    // Cancelling is not leaving: it says forget this attempt.
                    // The blocked answer was written to the report store so it
                    // would survive a trip away (spec 108 FR-008), so resetting
                    // the mutation alone would leave the store handing it
                    // straight back and the rows still on screen.
                    clearInstallReport(workspaceId, item);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  disabled={!canConfirm || installer.pending}
                  onClick={() => {
                    const request = toRequest(shownAnswers);
                    setAnswers(null);
                    installer.reset();
                    clearInstallReport(workspaceId, item);
                    installer.install(item.kind, item.key, request ?? {});
                  }}
                >
                  Install with these
                </Button>
              </div>
            </section>
          )}

          {installer.error && (
            <p role="alert" className="text-destructive text-sm">
              {installer.error}
            </p>
          )}

          {remover.error && (
            <p role="alert" className="text-destructive text-sm">
              {remover.error}
            </p>
          )}

          {/* Why a present item cannot be taken back out from here, when the
              reader would otherwise have to guess. */}
          {removalBlockedBecause(item) && (
            <p className="text-muted-foreground text-sm">
              {removalBlockedBecause(item)}
            </p>
          )}
        </SheetBody>

        {(action ?? removable) && !blocked && canWork && (
          <SheetFooter>
            {confirmingRemoval ? (
              <div className="flex w-full flex-col gap-2">
                <p role="status" className="text-sm">
                  Remove {item.key} from this workspace? Its folder is deleted.
                  {neededBy.length > 0 && (
                    <>
                      {' '}
                      {neededBy.length === 1
                        ? `${neededBy[0]} is installed here and names it.`
                        : `${String(neededBy.length)} installed workflows name it: ${neededBy.join(', ')}.`}
                    </>
                  )}
                </p>
                <div className="flex justify-end gap-2 max-md:flex-col-reverse max-md:[&>*]:w-full">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setConfirmingRemoval(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    disabled={remover.pending}
                    onClick={() => {
                      remover.uninstall(item.kind, item.key);
                      setConfirmingRemoval(false);
                    }}
                  >
                    <Trash2Icon aria-hidden="true" />
                    Remove
                  </Button>
                </div>
              </div>
            ) : (
              // On a phone the footer stacks full width, the main action on top
              // (docs/phone.md PH-03, spec 278).
              <div className="flex w-full items-center justify-between gap-2 max-md:flex-col-reverse max-md:items-stretch max-md:[&>span:empty]:hidden">
                {removable ? (
                  <Button
                    variant="ghost"
                    disabled={remover.pending}
                    onClick={() => {
                      remover.reset();
                      setConfirmingRemoval(true);
                    }}
                  >
                    <Trash2Icon aria-hidden="true" />
                    Remove
                  </Button>
                ) : (
                  <span />
                )}
                {action && (
                  <Button
                    disabled={installer.pending}
                    onClick={() => {
                      installer.reset();
                      if (action === 'install')
                        installer.install(item.kind, item.key);
                      else installer.update(item.kind, item.key);
                    }}
                  >
                    {action === 'install' ? (
                      <DownloadIcon aria-hidden="true" />
                    ) : (
                      <RefreshCwIcon aria-hidden="true" />
                    )}
                    {action === 'install' ? 'Install' : 'Update'}
                  </Button>
                )}
              </div>
            )}
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}

/**
 * Who shared this item, and — for them or an org admin — the way to take it back
 * out of the Library, with the linked pieces only it uses (specs 206, 213).
 * Installed copies stay where they are; the confirmation says so, because that
 * is the question a person hesitates over.
 */
function SharedBy({
  workspaceId,
  item,
  onTakenDown,
}: {
  workspaceId: string;
  item: LibraryItemView;
  onTakenDown: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const me = useCurrentUser().data;
  const when = item.shared_at ? ` on ${item.shared_at.slice(0, 10)}` : '';
  // Whether the reader shared it, by name: `shared_by` is a display name (no
  // id is sent), so the account menu's own rule for a name is what matches it
  // (spec 213 research §3). Getting it wrong only changes the wording.
  const mine =
    me !== undefined &&
    item.shared_by === displayNameOf(me.display_name, me.email);

  return (
    <div className="flex flex-col gap-2">
      <p className="text-muted-foreground text-sm">
        Shared by {item.shared_by ?? 'a member'}
        {when}.
      </p>
      {item.can_take_down &&
        (confirming ? (
          <TakeDownConfirm
            workspaceId={workspaceId}
            item={item}
            sharer={mine ? null : (item.shared_by ?? 'someone')}
            onCancel={() => setConfirming(false)}
            onTakenDown={(result) => {
              toast.success(takenDownMessage(result));
              onTakenDown();
            }}
          />
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="w-fit"
            onClick={() => setConfirming(true)}
          >
            Take down from the Library
          </Button>
        ))}
    </div>
  );
}
